import React, { useCallback } from 'react';
import { message } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import _ from 'lodash';

import { Button, Typography } from 'ui-kit';
import { coHostResendInvitation, ICoHost } from 'api/co-hosts';
import Table from 'components/Table';
import RemoveCoHostButton from 'components/CoHosts/RemoveCoHostButton';
import { ECoHostStatus } from 'components/CoHosts/constants';
import EditCoHostButtonModal from 'components/CoHosts/EditCoHostButtonModal';
import { useSignedUser } from 'api/auth';

export interface ICoHostsTable {
  coHosts: ICoHost[];
  onRemove: ({ id }: { id: number }) => void;
}

const CoHostsTable = ({ coHosts, onRemove }: ICoHostsTable) => {
  const { data: user } = useSignedUser();
  const handleResendCoHostInvitation = useCallback(
    ({ id }: { id: number }) =>
      coHostResendInvitation({ id })
        .then(() => message.success('Invitation resent successfully.'))
        .catch((error) => message.error(error.message)),
    [],
  );

  return (
    <>
      <Table
        scroll={{ x: '100%' }}
        className=""
        rowKey={(record) => record.id}
        columns={[
          {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
          },
          {
            title: 'Phone Number',
            dataIndex: 'phone_number',
            key: 'phone_number',
          },
          {
            title: 'SMS notifications',
            dataIndex: 'notification',
            key: 'notification',
            render: (t, coHost: ICoHost) => {
              return (
                <div
                  style={{
                    display: 'flex',
                    gap: '1rem',
                  }}
                >
                  <Typography variant="caption">
                    {coHost.is_request_sms_enabled ? (
                      <CheckCircleOutlined
                        style={{
                          color: 'green',
                          fontSize: '14px',
                        }}
                      />
                    ) : (
                      <CloseCircleOutlined
                        style={{
                          color: 'red',
                          fontSize: '14px',
                        }}
                      />
                    )}{' '}
                    Request and Approve
                  </Typography>
                  <Typography variant="caption">
                    {coHost.is_sales_sms_enabled ? (
                      <CheckCircleOutlined
                        style={{
                          color: 'green',
                          fontSize: '14px',
                        }}
                      />
                    ) : (
                      <CloseCircleOutlined
                        style={{
                          color: 'red',
                          fontSize: '14px',
                        }}
                      />
                    )}{' '}
                    Sales
                  </Typography>
                  <Typography variant="caption">
                    {coHost.is_marketing_sms_enabled ? (
                      <CheckCircleOutlined
                        style={{
                          color: 'green',
                          fontSize: '14px',
                        }}
                      />
                    ) : (
                      <CloseCircleOutlined
                        style={{
                          color: 'red',
                          fontSize: '14px',
                        }}
                      />
                    )}{' '}
                    News
                  </Typography>
                </div>
              );
            },
          },
          {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
          },
          {
            title: 'Actions',
            dataIndex: 'actions',
            key: 'actions',
            render: (t, coHost: ICoHost) => {
              if (
                _.includes([ECoHostStatus.PENDING, ECoHostStatus.INVITATION_EXPIRED], coHost.status)
              ) {
                return (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <EditCoHostButtonModal coHost={coHost} />
                    {user?.cohost_id !== coHost.id && (
                      <RemoveCoHostButton coHostId={coHost.id} onRemove={onRemove} />
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleResendCoHostInvitation({ id: coHost.id })}
                    >
                      Resend Invitation
                    </Button>
                  </div>
                );
              } else if (coHost.status === ECoHostStatus.ACCEPTED) {
                return (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <EditCoHostButtonModal coHost={coHost} />
                    {user?.cohost_id !== coHost.id && (
                      <RemoveCoHostButton coHostId={coHost.id} onRemove={onRemove} />
                    )}
                  </div>
                );
              }

              return null;
            },
          },
        ]}
        dataSource={coHosts}
      ></Table>
    </>
  );
};

export default CoHostsTable;
